import React, { useState, useEffect, useRef, useContext } from "react";
import { Button, InputAdornment, Box, Divider, Switch, FormGroup, FormControlLabel } from "@mui/material";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import CheckboxValidatorElement from "./CheckboxValidatorElement";
import { Message, Lock, VkBlue, Google, ShowPass, HidePass, Link, SocIconYandex, SocIconFB } from "../../assets/images/icons";
import { styled } from "@mui/material/styles";
import REST from "../../libs/API";
import { getText } from "../../libs/utils";
import { openSocAuth } from "../../libs/utils";
import { UserDataContext } from "../../App";

const MyDivider = styled(Divider)(() => ({
  margin: "20px 0 17px",
}));

const Register = ({ setActiveForm, socialLinks = {} }) => {
  const fieldRefs = useRef({});
  const [loading, setLoading] = useState(false);
  const userData = useContext(UserDataContext);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    passwordRepeat: "",
    terms: false,
    link: ""
  });

  const [showPasswords, setShowPasswords] = useState({
    password: "password",
    passwordRepeat: "password",
  });

  const [openMonitoring, setOpenMonitoring] = useState(false);

  useEffect(() => {
    ValidatorForm.addValidationRule('isPasswordMatch', (value) => {
      if (value !== formData.password) {
        return false;
      }
      return true;
    }, []);

    ValidatorForm.addValidationRule('isTruthy', value => value);

  }, [formData.password]);

  useEffect(() => {
    return () => {
      ValidatorForm.removeValidationRule('isPasswordMatch');
      ValidatorForm.removeValidationRule('isTruthy');
    };
  }, []);

  const handlerSubmit = () => {
    setLoading(true);
    const { email, password, link } = formData;
    REST
      .register(email, password, { link, refCode: userData.refCode })
      .then(data => {
        if (data.success) {
          setActiveForm('RegisterApprove');
        } else {
          window.showMessage(data.data[0].message);
        }
        setLoading(false);
      });
  }

  return (
    <ValidatorForm
      onSubmit={handlerSubmit}
      instantValidate={false}
    >
      <TextValidator
        fullWidth
        placeholder="E-mail"
        variant="standard"
        name="email"
        ref={el => fieldRefs.current['email'] = el}
        onBlur={event => fieldRefs.current[event.target.name].validate(event.target.value)}
        onChange={(event) =>
          setFormData({
            ...formData,
            [event.target.name]: event.target.value,
          })
        }
        validators={["required", "isEmail"]}
        errorMessages={[
          getText('inputRequired'),
          getText('loginIncorectMail'),
        ]}
        value={formData.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Message />
            </InputAdornment>
          ),
        }}
      />

      <TextValidator
        fullWidth
        placeholder={getText('loginFormPassLabel')}
        variant="standard"
        name="password"
        ref={el => fieldRefs.current['password'] = el}
        onBlur={event => fieldRefs.current[event.target.name].validate(event.target.value)}
        type={showPasswords.password}
        onChange={(event) =>
          setFormData({
            ...formData,
            [event.target.name]: event.target.value,
          })
        }
        validators={["required", "minStringLength:8"]}
        errorMessages={[getText('inputRequired'), getText('inputPassMin')]}
        value={formData.password}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment
              position="start"
              style={{ cursor: "pointer" }}
              onClick={() =>
                showPasswords.password === "password"
                  ? setShowPasswords({ ...showPasswords, password: "text" })
                  : setShowPasswords({ ...showPasswords, password: "password" })
              }
            >
              {showPasswords.password === "password" ? <ShowPass /> : <HidePass />}
            </InputAdornment>
          ),
        }}
      />

      <TextValidator
        fullWidth
        placeholder={getText('loginFormPassRepeat')}
        variant="standard"
        name="passwordRepeat"
        ref={el => fieldRefs.current['passwordRepeat'] = el}
        onBlur={event => fieldRefs.current[event.target.name].validate(event.target.value)}
        type={showPasswords.passwordRepeat}
        onChange={(event) =>
          setFormData({
            ...formData,
            [event.target.name]: event.target.value,
          })
        }
        validators={['isPasswordMatch', 'required']}
        errorMessages={[getText('inputPassDistinct'), getText('inputRequired')]}
        value={formData.passwordRepeat}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment
              position="start"
              style={{ cursor: "pointer" }}
              onClick={() =>
                showPasswords.passwordRepeat === "password"
                  ? setShowPasswords({ ...showPasswords, passwordRepeat: "text" })
                  : setShowPasswords({ ...showPasswords, passwordRepeat: "password" })
              }
            >
              {showPasswords.passwordRepeat === "password" ? <ShowPass /> : <HidePass />}
            </InputAdornment>
          ),
        }}
      />

      <CheckboxValidatorElement
        name="terms"
        ref={el => fieldRefs.current['terms'] = el}
        validators={["isTruthy"]}
        checked={formData.terms}
        value={formData.terms}
        style={{ margin: "8px 0 5px" }}
        onChange={(event) => {
          setFormData({ ...formData, terms: !formData.terms });
          event.target.checked && fieldRefs.current['terms'].makeValid();
        }}
        label={
          <Box sx={{ color: formData.terms && "#E43C3C" }}>
            {getText('orderAgree1')}{" "}
            <a href="/rules" target="_blank">{getText("orderAgree2")}</a>
          </Box>
        }
      />

      <FormGroup>
        <FormControlLabel control={<Switch onChange={() => setOpenMonitoring(!openMonitoring)} />} label={getText('singMonitorLinkLbl')} />
      </FormGroup>

      {openMonitoring &&
        <TextValidator
          fullWidth
          placeholder={getText('regAsMonPlaceholder')}
          variant="standard"
          name="link"
          onChange={(event) =>
            setFormData({
              ...formData,
              [event.target.name]: event.target.value,
            })
          }
          validators={["required"]}
          errorMessages={[getText('inputRequired')]}
          value={formData.link}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Link />
              </InputAdornment>
            ),
          }}
        />
      }

      <Button
        variant="contained"
        fullWidth
        size="large"
        type="submit"
        disabled={loading}
      >
        {getText('registerButton')}
      </Button>

      <Box textAlign="center">
        <span className="link-secondary" onClick={() => setActiveForm("Auth")}>
          {getText('regGoLogin')}
        </span>
      </Box>

      {Object.keys(socialLinks).length > 0 && <MyDivider />}

      <Box style={{ justifyContent: "space-evenly", display: 'flex', flexWrap: 'nowrap' }}>
        {false && socialLinks.vk && (
          <Box sx={{ cursor: 'pointer' }} onClick={() => openSocAuth(socialLinks, "vk")}>
            <VkBlue />
          </Box>
        )}
        {false && socialLinks.google && (
          <Box sx={{ cursor: 'pointer' }} onClick={() => openSocAuth(socialLinks, "google")}>
            <Google />
          </Box>
        )}
        {socialLinks.yandex && (
          <Box sx={{ cursor: 'pointer' }} onClick={() => openSocAuth(socialLinks, "yandex")}>
            <SocIconYandex />
          </Box>
        )}
        {socialLinks.facebook && (
          <Box sx={{ cursor: 'pointer' }} onClick={() => openSocAuth(socialLinks, "facebook")}>
            <SocIconFB />
          </Box>
        )}
      </Box>
    </ValidatorForm>
  );
};

export default Register;
